import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CircularProgress,
  Alert,
  InputAdornment,
  MenuItem,
  Select,
  Chip,
  IconButton,
  Tooltip,
  Avatar,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import CustomerCard from '../components/CustomerCard';
import * as XLSX from 'xlsx-js-style';

interface Customer {
  _id: string;
  firstName?: string;
  lastName?: string;
  gsm?: string;
  sabitTel?: string;
  email?: string;
  il?: string;
  ilce?: string;
  adres?: string;
  tc?: string;
  notes?: Array<{
    content: string;
    createdAt: string;
    callId?: string;
  }>;
  finishCodes?: Array<{
    code: string;
    createdAt: string;
    callId?: string;
    agentId?: string;
  }>;
  createdAt: string;
  updatedAt?: string;
}

const CustomerManagement: React.FC = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      navigate('/login');
      return;
    }
    loadCustomers();
  }, [navigate]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/customers', { params: { limit: 100 } });
      const data = res?.data;
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.customers)
            ? data.customers
            : [];
      setCustomers(list);
    } catch (e: any) {
      console.error('❌ Customers API error:', e);
      setError(e?.message || 'Müşteri verileri alınamadı');
    } finally {
      setLoading(false);
    }
  };

  const getCustomerStatus = (customer: Customer): string => {
    if (!customer.finishCodes || customer.finishCodes.length === 0) {
      return 'Beklemede';
    }
    const latestCode = customer.finishCodes[customer.finishCodes.length - 1];
    return latestCode.code;
  };

  const getStatusColor = (status: string): { bg: string; text: string; border: string } => {
    const statusMap: Record<string, { bg: string; text: string; border: string }> = {
      'Görüşme Yapıldı': { bg: '#DBEAFE', text: '#1E40AF', border: '#BFDBFE' },
      'Görüşüldü': { bg: '#DBEAFE', text: '#1E40AF', border: '#BFDBFE' },
      'Aktif': { bg: '#D1FAE5', text: '#065F46', border: '#A7F3D0' },
      'Ulaşılamadı': { bg: '#FEE2E2', text: '#991B1B', border: '#FECACA' },
      'Ses Yok': { bg: '#FEF3C7', text: '#92400E', border: '#FDE68A' },
      'Meşgul': { bg: '#FED7AA', text: '#9A3412', border: '#FDBA74' },
      'Beklemede': { bg: '#F3F4F6', text: '#374151', border: '#E5E7EB' },
      'Pasif': { bg: '#F3F4F6', text: '#6B7280', border: '#D1D5DB' }
    };
    return statusMap[status] || statusMap['Beklemede'];
  };

  const getInitials = (firstName?: string, lastName?: string): string => {
    if (!firstName && !lastName) return '?';
    const first = firstName?.charAt(0)?.toUpperCase() || '';
    const last = lastName?.charAt(0)?.toUpperCase() || '';
    return first + last;
  };

  const getAvatarColor = (name?: string): string => {
    if (!name) return '#EC4899';
    const colors = ['#6366F1', '#EC4899', '#8B5CF6', '#F59E0B', '#14B8A6', '#3B82F6', '#F97316', '#6B7280'];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const formatPhoneNumber = (phone?: string): string => {
    if (!phone) return '-';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('0')) {
      return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7, 9)} ${cleaned.slice(9)}`;
    }
    return phone;
  };

  const formatDateTime = (dateString?: string): { date: string; time: string } => {
    if (!dateString) return { date: '-', time: '' };
    try {
      const date = new Date(dateString);
      const dateStr = date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
      const timeStr = date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      return { date: dateStr, time: timeStr };
    } catch {
      return { date: '-', time: '' };
    }
  };

  const getLastNote = (customer: Customer): string => {
    if (!customer.notes || customer.notes.length === 0) return '-';
    const lastNote = customer.notes[customer.notes.length - 1];
    const content = lastNote.content || '-';
    const date = lastNote.createdAt;
    if (date) {
      try {
        const formatted = new Date(date).toLocaleString('tr-TR');
        return `${content} ${formatted}`;
      } catch {
        return content;
      }
    }
    return content;
  };

  const filteredCustomers = customers.filter((customer) => {
    const matchesSearch =
      !searchQuery ||
      (customer.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        customer.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        customer.gsm?.includes(searchQuery) ||
        customer._id?.includes(searchQuery));

    const status = getCustomerStatus(customer);
    const matchesStatus = statusFilter === 'all' || status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCustomers = filteredCustomers.slice(startIndex, endIndex);

  const handleStatusFilterChange = (event: SelectChangeEvent<string>) => {
    setStatusFilter(event.target.value);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEditClick = (customer: Customer) => {
    setSelectedCustomer(customer);
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setSelectedCustomer(null);
  };

  const handleCustomerUpdated = () => {
    // Reload customers after update
    loadCustomers();
    handleEditClose();
  };

  const handleExportExcel = () => {
    // Prepare data for export with all customer fields
    const exportData = filteredCustomers.map((customer, index) => {
      // Format all finish codes with timestamps
      const finishCodes = customer.finishCodes && customer.finishCodes.length > 0
        ? customer.finishCodes.map(fc => {
            const date = fc.createdAt ? new Date(fc.createdAt).toLocaleString('tr-TR') : '-';
            return `${fc.code} (${date})`;
          }).join('\n')
        : '-';
      
      // Format all notes with timestamps
      const notes = customer.notes && customer.notes.length > 0
        ? customer.notes.map(note => {
            const date = note.createdAt ? new Date(note.createdAt).toLocaleString('tr-TR') : '-';
            return `${note.content} (${date})`;
          }).join('\n')
        : '-';

      return {
        'ID': index + 1,
        'AD': customer.firstName || '-',
        'SOYAD': customer.lastName || '-',
        'CEP TELEFONU (GSM)': customer.gsm || '-',
        'SABİT TELEFON': customer.sabitTel || '-',
        'E-POSTA': customer.email || '-',
        'İL': customer.il || '-',
        'İLÇE': customer.ilce || '-',
        'ADRES': customer.adres || '-',
        'TC KİMLİK NO': customer.tc || '-',
        'OLUŞTURMA TARİHİ': new Date(customer.createdAt).toLocaleString('tr-TR'),
        'GÜNCELLEME TARİHİ': customer.updatedAt ? new Date(customer.updatedAt).toLocaleString('tr-TR') : '-',
        'TÜM FİNİSH CODELAR': finishCodes,
        'TÜM NOTLAR': notes
      };
    });

    // Create worksheet from data
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    
    // Set column widths
    ws['!cols'] = [
      { wch: 8 },  // SIRA
      { wch: 20 }, // AD
      { wch: 20 }, // SOYAD
      { wch: 20 }, // CEP TELEFONU (GSM)
      { wch: 20 }, // SABİT TELEFON
      { wch: 35 }, // E-POSTA
      { wch: 15 }, // İL
      { wch: 15 }, // İLÇE
      { wch: 50 }, // ADRES
      { wch: 15 }, // TC KİMLİK NO
      { wch: 22 }, // OLUŞTURMA TARİHİ
      { wch: 22 }, // GÜNCELLEME TARİHİ
      { wch: 50 }, // TÜM FİNİSH CODELAR
      { wch: 60 }  // TÜM NOTLAR
    ];

    // Get range
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    
    // Style the header row (row 1) with blue background
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const address = XLSX.utils.encode_col(C) + '1';
      if (!ws[address]) continue;
      
      ws[address].s = {
        font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 12 },
        fill: { fgColor: { rgb: '4F46E5' } },
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
        border: {
          top: { style: 'thin', color: { rgb: '000000' } },
          bottom: { style: 'thin', color: { rgb: '000000' } },
          left: { style: 'thin', color: { rgb: '000000' } },
          right: { style: 'thin', color: { rgb: '000000' } }
        }
      };
    }

    // Style all data cells with center alignment
    for (let R = range.s.r + 1; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[address]) continue;
        
        ws[address].s = {
          alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
          border: {
            top: { style: 'thin', color: { rgb: 'CCCCCC' } },
            bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
            left: { style: 'thin', color: { rgb: 'CCCCCC' } },
            right: { style: 'thin', color: { rgb: 'CCCCCC' } }
          }
        };
      }
    }

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Müşteriler');
    
    // Generate filename with current date
    const fileName = `Musteriler_${new Date().toISOString().split('T')[0]}.xlsx`;
    
    // Save file
    XLSX.writeFile(wb, fileName);
  };

  return (
    <Box
      sx={{
        bgcolor: '#F8F9FB',
        px: { xs: 2, sm: 4, md: 6, lg: 8 },
        py: 3,
        fontFamily: "'Inter', sans-serif",
        minHeight: '100vh'
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827', mb: 0.5 }}>
            Müşteri Yönetimi
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280' }}>
            Tüm müşteri kayıtlarını görüntüleyin, arayın ve yönetin.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon sx={{ fontSize: '18px !important' }} />}
            onClick={handleExportExcel}
            sx={{
              textTransform: 'none',
              borderColor: '#D1D5DB',
              color: '#374151',
              fontWeight: 500,
              fontSize: '0.875rem',
              '&:hover': { borderColor: '#9CA3AF', bgcolor: '#F9FAFB' }
            }}
          >
            Dışa Aktar
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: '18px !important' }} />}
            sx={{
              textTransform: 'none',
              bgcolor: '#4F46E5',
              fontWeight: 500,
              fontSize: '0.875rem',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#4338CA', boxShadow: 'none' }
            }}
          >
            Yeni Müşteri
          </Button>
        </Box>
      </Box>

      {/* Search & Filter Bar */}
      <Card
        sx={{
          mb: 3,
          borderRadius: '12px',
          border: '1px solid #E5E7EB',
          boxShadow: 'none'
        }}
      >
        <Box sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
            <TextField
              fullWidth
              placeholder="Ara (isim, telefon veya ID)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#9CA3AF', fontSize: 20 }} />
                  </InputAdornment>
                ),
                sx: {
                  bgcolor: '#F9FAFB',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#D1D5DB' },
                  '&.Mui-focused fieldset': { borderColor: '#4F46E5' }
                }
              }}
            />
            <Select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              size="small"
              sx={{
                minWidth: 160,
                bgcolor: '#FFFFFF',
                '& fieldset': { borderColor: '#E5E7EB' },
                '&:hover fieldset': { borderColor: '#D1D5DB' },
                '&.Mui-focused fieldset': { borderColor: '#4F46E5' }
              }}
            >
              <MenuItem value="all">Tüm Durumlar</MenuItem>
              <MenuItem value="Aktif">Aktif</MenuItem>
              <MenuItem value="Pasif">Pasif</MenuItem>
              <MenuItem value="Beklemede">Beklemede</MenuItem>
              <MenuItem value="Görüşme Yapıldı">Görüşüldü</MenuItem>
              <MenuItem value="Ulaşılamadı">Ulaşılamadı</MenuItem>
            </Select>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon sx={{ fontSize: '18px !important' }} />}
              onClick={loadCustomers}
              sx={{
                textTransform: 'none',
                borderColor: '#D1D5DB',
                color: '#4F46E5',
                fontWeight: 500,
                fontSize: '0.875rem',
                whiteSpace: 'nowrap',
                '&:hover': { borderColor: '#4F46E5', bgcolor: '#EEF2FF' }
              }}
            >
              YENİLE
            </Button>
          </Box>
        </Box>
      </Card>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      {!loading && !error && (
        <Box
          sx={{
            bgcolor: '#FFFFFF',
            borderRadius: '12px',
            border: '1px solid #E5E7EB',
            overflow: 'visible',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
          }}
        >
          <Box sx={{ overflowX: 'auto', maxHeight: 'none' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#F9FAFB' }}>
                <tr>
                  <th
                    style={{
                      padding: '16px 24px',
                      textAlign: 'left',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: '#6B7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #E5E7EB'
                    }}
                  >
                    Ad Soyad
                  </th>
                  <th
                    style={{
                      padding: '16px 24px',
                      textAlign: 'left',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: '#6B7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #E5E7EB'
                    }}
                  >
                    İletişim (GSM)
                  </th>
                  <th
                    style={{
                      padding: '16px 24px',
                      textAlign: 'left',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: '#6B7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #E5E7EB'
                    }}
                  >
                    Son İşlem
                  </th>
                  <th
                    style={{
                      padding: '16px 24px',
                      textAlign: 'left',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: '#6B7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #E5E7EB'
                    }}
                  >
                    Durum / Notlar
                  </th>
                  <th
                    style={{
                      padding: '16px 24px',
                      textAlign: 'left',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: '#6B7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #E5E7EB'
                    }}
                  >
                    Kayıt Tarihi
                  </th>
                  <th
                    style={{
                      padding: '16px 24px',
                      textAlign: 'right',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: '#6B7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #E5E7EB'
                    }}
                  >
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedCustomers.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: '48px 24px', textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ color: '#6B7280' }}>
                        Gösterilecek müşteri bulunamadı.
                      </Typography>
                    </td>
                  </tr>
                ) : (
                  paginatedCustomers.map((customer) => {
                    const status = getCustomerStatus(customer);
                    const statusColors = getStatusColor(status);
                    const { date, time } = formatDateTime(customer.createdAt);
                    const latestFinishCode = customer.finishCodes?.[customer.finishCodes.length - 1];
                    const finishCodeDate = latestFinishCode?.createdAt
                      ? formatDateTime(latestFinishCode.createdAt)
                      : null;

                    return (
                      <tr
                        key={customer._id}
                        style={{
                          borderBottom: '1px solid #E5E7EB',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#F9FAFB')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                      >
                        <td style={{ padding: '16px 24px' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                              sx={{
                                width: 40,
                                height: 40,
                                bgcolor: getAvatarColor(customer.firstName || customer.lastName),
                                fontSize: '0.875rem',
                                fontWeight: 700
                              }}
                            >
                              {getInitials(customer.firstName, customer.lastName)}
                            </Avatar>
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  color: customer.firstName || customer.lastName ? '#111827' : '#9CA3AF',
                                  fontStyle: customer.firstName || customer.lastName ? 'normal' : 'italic'
                                }}
                              >
                                {customer.firstName || customer.lastName
                                  ? `${customer.firstName || ''} ${customer.lastName || ''}`.trim()
                                  : 'İsimsiz'}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                ID: ...{customer._id.slice(-7)}
                              </Typography>
                            </Box>
                          </Box>
                        </td>
                        <td style={{ padding: '16px 24px' }}>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace', color: '#111827', fontSize: '0.875rem' }}
                          >
                            {formatPhoneNumber(customer.gsm)}
                          </Typography>
                        </td>
                        <td style={{ padding: '16px 24px' }}>
                          <Tooltip title={getLastNote(customer)} placement="top">
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#6B7280',
                                fontSize: '0.875rem',
                                maxWidth: 250,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {getLastNote(customer)}
                            </Typography>
                          </Tooltip>
                        </td>
                        <td style={{ padding: '16px 24px' }}>
                          <Box>
                            <Chip
                              label={status}
                              size="small"
                              sx={{
                                bgcolor: statusColors.bg,
                                color: statusColors.text,
                                border: `1px solid ${statusColors.border}`,
                                fontWeight: 500,
                                fontSize: '0.75rem',
                                height: 24
                              }}
                            />
                            {finishCodeDate && (
                              <Typography variant="caption" sx={{ color: '#9CA3AF', display: 'block', mt: 0.5 }}>
                                ({finishCodeDate.date} {finishCodeDate.time})
                              </Typography>
                            )}
                          </Box>
                        </td>
                        <td style={{ padding: '16px 24px' }}>
                          <Typography variant="body2" sx={{ color: '#111827', fontSize: '0.875rem' }}>
                            {date}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6B7280' }}>
                            {time}
                          </Typography>
                        </td>
                        <td style={{ padding: '16px 24px', textAlign: 'right' }}>
                          <IconButton 
                            size="small" 
                            onClick={() => handleEditClick(customer)}
                            sx={{ color: '#9CA3AF', '&:hover': { color: '#4F46E5' } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </Box>

          {/* Pagination */}
          {filteredCustomers.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2.5,
                borderTop: '1px solid #E5E7EB'
              }}
            >
              <Typography variant="body2" sx={{ color: '#374151' }}>
                Toplam <strong>{filteredCustomers.length}</strong> kayıttan <strong>{startIndex + 1}</strong> ile{' '}
                <strong>{Math.min(endIndex, filteredCustomers.length)}</strong> arası gösteriliyor
              </Typography>
              <Box sx={{ display: 'flex', gap: 0 }}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                  sx={{
                    minWidth: 32,
                    px: 1,
                    borderColor: '#D1D5DB',
                    color: '#6B7280',
                    borderRadius: '6px 0 0 6px',
                    '&:hover': { bgcolor: '#F9FAFB', borderColor: '#D1D5DB' },
                    '&:disabled': { opacity: 0.5, cursor: 'not-allowed' }
                  }}
                >
                  ‹
                </Button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <Button
                      key={page}
                      variant={currentPage === page ? "contained" : "outlined"}
                      size="small"
                      onClick={() => handlePageChange(page)}
                      sx={{
                        minWidth: 32,
                        px: 1.5,
                        ...(currentPage === page ? {
                          bgcolor: '#EEF2FF',
                          color: '#4F46E5',
                          borderRadius: 0,
                          border: '1px solid #4F46E5',
                          boxShadow: 'none',
                          '&:hover': { bgcolor: '#E0E7FF', boxShadow: 'none' }
                        } : {
                          borderColor: '#D1D5DB',
                          color: '#6B7280',
                          borderRadius: 0,
                          '&:hover': { bgcolor: '#F9FAFB', borderColor: '#D1D5DB' }
                        })
                      }}
                    >
                      {page}
                    </Button>
                  );
                })}
                <Button
                  variant="outlined"
                  size="small"
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                  sx={{
                    minWidth: 32,
                    px: 1,
                    borderColor: '#D1D5DB',
                    color: '#6B7280',
                    borderRadius: '0 6px 6px 0',
                    '&:hover': { bgcolor: '#F9FAFB', borderColor: '#D1D5DB' },
                    '&:disabled': { opacity: 0.5, cursor: 'not-allowed' }
                  }}
                >
                  ›
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Edit Customer Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={handleEditClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            maxHeight: '90vh',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 2,
          borderBottom: '1px solid #E5E7EB',
          bgcolor: '#F9FAFB'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
            Müşteri Detayları
          </Typography>
          <IconButton 
            onClick={handleEditClose}
            size="small"
            sx={{ color: '#6B7280', '&:hover': { color: '#111827', bgcolor: '#E5E7EB' } }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          {selectedCustomer && (
            <Box sx={{ 
              height: 'calc(90vh - 140px)', 
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#F3F4F6',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#D1D5DB',
                borderRadius: '4px',
                '&:hover': {
                  background: '#9CA3AF',
                }
              }
            }}>
              <CustomerCard
                phoneNumber={selectedCustomer.gsm || ''}
                callId={null}
                isCallActive={true}
                showCustomerCard={true}
                requireFinishCode={false}
                onFinish={handleCustomerUpdated}
                onClose={handleEditClose}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CustomerManagement;


